import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Shield, Target, Sun, Moon } from 'lucide-react';
import { LogoutHeaderButton } from '../auth/AuthWrapper';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';

/**
 * Landing page component for the EAI (Enterprise AI) evaluation platform
 * Provides an introduction to the platform and guides users to start with the tutorial
 */
export default function Landing({ onStartTutorial }) {
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('darkMode');
    return stored === 'true';
  });
  
  const [highlightTutorial, setHighlightTutorial] = useState(false);
  
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', String(darkMode));
  }, [darkMode]);

  // Check if user came from Get Help button
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('help') === 'true') {
      setHighlightTutorial(true);
      // Remove the glow after 4 seconds
      setTimeout(() => setHighlightTutorial(false), 4000);
    }
  }, []);

  const handleLogin = () => {
    instance
      .loginRedirect({
        scopes: [
          "openid",
          "profile",
          "email",
          "api://32191e63-e253-48de-9ea1-a5337e236fe6/.default",
        ],
      })
      .catch((e) => console.error(e));
  };

  const LoginButton = () => (
    <button
      onClick={handleLogin}
      className="px-3 py-2 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors duration-200"
    >
      Sign In
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950">
      <style>{`
        :root {
          --primary: 220 70% 15%;
          --primary-foreground: 0 0% 98%;
          --secondary: 158 64% 52%;
          --secondary-foreground: 0 0% 9%;
          --accent: 220 14% 96%;
          --accent-foreground: 220 8% 46%;
          --muted: 220 14% 96%;
          --muted-foreground: 220 8% 46%;
        }
        
        @keyframes tutorial-glow {
          0%, 100% {
            box-shadow: 0 0 5px rgba(16, 185, 129, 0.5), 0 0 10px rgba(16, 185, 129, 0.3), 0 0 15px rgba(16, 185, 129, 0.1);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 20px rgba(16, 185, 129, 0.8), 0 0 30px rgba(16, 185, 129, 0.6), 0 0 40px rgba(16, 185, 129, 0.4);
            transform: scale(1.05);
          }
        }
        
        @keyframes tutorial-glow-dark {
          0%, 100% {
            box-shadow: 0 0 5px rgba(16, 185, 129, 0.7), 0 0 10px rgba(16, 185, 129, 0.5), 0 0 15px rgba(16, 185, 129, 0.3);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 20px rgba(16, 185, 129, 1), 0 0 30px rgba(16, 185, 129, 0.8), 0 0 40px rgba(16, 185, 129, 0.6);
            transform: scale(1.05);
          }
        }
        
        .tutorial-glow {
          animation: tutorial-glow 1s ease-in-out infinite;
        }
        
        .dark .tutorial-glow {
          animation: tutorial-glow-dark 1s ease-in-out infinite;
        }
      `}</style>
      
      {/* Header */}
      <header className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-b border-slate-200/60 dark:border-slate-800 px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center">
              <img 
                src="/SVG-08.svg" 
                alt="EAI Logo" 
                className="w-10 h-10 object-contain"
              />
            </div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">EAI Evaluation Platform</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              onClick={() => setDarkMode((v) => !v)}
              className="inline-flex items-center justify-center h-9 w-9 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {darkMode ? (
                <Sun className="w-5 h-5 text-slate-700 dark:text-slate-200" />
              ) : (
                <Moon className="w-5 h-5 text-slate-700 dark:text-slate-200" />
              )}
            </button>
            <button
              type="button"
              onClick={onStartTutorial}
              className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all duration-500 ${
                highlightTutorial 
                  ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 shadow-lg scale-105 tutorial-glow' 
                  : 'border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              {isAuthenticated ? 'Start Tutorial' : 'Take a Tour'}
            </button>
            {isAuthenticated ? (
              <LogoutHeaderButton className="px-3 py-2 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200" />
            ) : (
              <LoginButton />
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex items-center justify-center p-6" style={{ minHeight: 'calc(100vh - 80px)' }}>
      <div className="max-w-4xl mx-auto text-center">
        {/* Hero Section */}
        <div className="mb-12" data-landing-hero>
          <div className="flex items-center justify-center mb-6">
            <div className="w-32 h-32 flex items-center justify-center">
              <img 
                src="/SVG-12.svg" 
                alt="EAI Logo" 
                className="w-32 h-32 object-contain dark:hidden"
              />
              <img 
                src="/SVG-13.svg" 
                alt="EAI Logo" 
                className="w-32 h-32 object-contain hidden dark:block"
              />
            </div>
          </div>
          
          <h1 className="text-5xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            Welcome to <span className="text-emerald-600">EAI</span>
          </h1>
          
          <p className="text-xl text-slate-600 dark:text-slate-300 mb-2">
            Enterprise AI Evaluation Platform
          </p>
          
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
            A comprehensive platform for testing, evaluating, and validating your AI systems. 
            Build robust test cases, run comprehensive evaluations, and ensure your AI meets enterprise standards.
          </p>
        </div>

        {/* Feature Highlights */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200/60 dark:border-slate-700 shadow-lg">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center mb-4 mx-auto">
              <Target className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">Precision Testing</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Create detailed test cases to evaluate AI performance across various scenarios
            </p>
          </div>
          
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200/60 dark:border-slate-700 shadow-lg">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center mb-4 mx-auto">
              <Zap className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">Fast Evaluation</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Run comprehensive evaluations quickly and get detailed performance insights
            </p>
          </div>
          
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200/60 dark:border-slate-700 shadow-lg">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center mb-4 mx-auto">
              <Shield className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">Enterprise Ready</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Built for enterprise scale with security, compliance, and reliability in mind
            </p>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-200/60 dark:border-slate-700 shadow-xl" data-landing-cta>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            Ready to Get Started?
          </h2>
          
          {isAuthenticated ? (
            <>
              <p className="text-slate-600 dark:text-slate-300 mb-6">
                Click the <strong>tutorial</strong> in the top right corner to learn how to use the platform, 
                or jump straight into creating your first test case.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={onStartTutorial}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-500 shadow-lg hover:shadow-xl ${
                    highlightTutorial 
                      ? 'bg-emerald-500 hover:bg-emerald-600 text-white border-2 border-emerald-300 tutorial-glow' 
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  }`}
                >
                  Start Tutorial
                  <ArrowRight className="w-5 h-5" />
                </button>
                
                <Link
                  to="/CreateTest"
                  data-tour="create-test-case"
                  className="flex items-center gap-2 bg-slate-600 hover:bg-slate-700 text-white px-6 py-3 rounded-xl font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
                >
                  Create Test Case
                  <img src="/SVG-06.svg" alt="EAI" className="w-5 h-5 object-contain" />
                </Link>
              </div>
            </>
          ) : (
            <>
              <p className="text-slate-600 dark:text-slate-300 mb-6">
                Sign in to access the full platform and start building test cases, running evaluations, and analyzing results.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={handleLogin}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
                >
                  Sign In to Get Started
                  <ArrowRight className="w-5 h-5" />
                </button>
                
                <button
                  onClick={onStartTutorial}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-500 shadow-lg hover:shadow-xl ${
                    highlightTutorial 
                      ? 'bg-slate-500 hover:bg-slate-600 text-white border-2 border-slate-300 tutorial-glow' 
                      : 'bg-slate-600 hover:bg-slate-700 text-white'
                  }`}
                >
                  Take a Tour
                  <img src="/SVG-06.svg" alt="EAI" className="w-5 h-5 object-contain" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 text-sm text-slate-500 dark:text-slate-400">
          <p>Enterprise AI Evaluation Platform • Secure • Scalable • Reliable</p>
        </div>
      </div>
      </div>
    </div>
  );
}
